/**
 * Every toast in the app: `message.success('Saved')`, and likewise `.error`,
 * `.warning`, `.info`.
 *
 * **Never import `ElMessage` outside `src/shared`** — the point of this wrapper is that
 * element-plus is referenced in the shared layer only, and ESLint enforces it in the FSD
 * layers. A refused mutation goes through `showMutationError` instead, which stays quiet
 * when the axios interceptor has already spoken; background work with nobody waiting on it
 * goes through `showGlobalError`.
 *
 * Pass `action` for a failure the user can do something about. Anything else element-plus
 * takes (`duration`, `showClose`) falls through.
 */

import { h } from 'vue'
import { ElMessage, type MessageHandler } from 'element-plus'

type MessageOptions = {
  duration?: number
  showClose?: boolean
  /**
   * A control rendered after the text, for a failure the user can act on rather than
   * only read. Pressing it closes the toast and runs `onClick`.
   *
   * A toast carrying one stays up longer than the element-plus default of 3s and gains a
   * close button: three seconds is enough to read a sentence or to aim at a button, not
   * both, and a control that disappears mid-reach is worse than none.
   */
  action?: { label: string; onClick: () => void }
}

/** Long enough to read the sentence and then decide, without parking a toast forever. */
const ACTION_DURATION_MS = 10000

function show(
  type: 'success' | 'error' | 'warning' | 'info',
  text: string,
  options?: MessageOptions
) {
  const { action, ...rest } = options ?? {}

  if (!action) {
    ElMessage({ type, message: text, ...rest })
    return
  }

  // Read only from the click handler, which cannot run before the toast it closes
  // exists — so the self-reference inside the initializer is safe.
  const handle: MessageHandler = ElMessage({
    type,
    duration: ACTION_DURATION_MS,
    showClose: true,
    ...rest,
    message: h('span', [
      text,
      h(
        'button',
        {
          type: 'button',
          // Styled here rather than in a stylesheet: this VNode has no SFC to carry a
          // scoped block, and the toast is the only thing that renders it.
          style: {
            marginLeft: '8px',
            padding: '0',
            border: '0',
            background: 'none',
            color: 'inherit',
            font: 'inherit',
            textDecoration: 'underline',
            cursor: 'pointer'
          },
          onClick: () => {
            handle.close()
            action.onClick()
          }
        },
        action.label
      )
    ])
  })
}

export const message = {
  success(text: string, options?: MessageOptions) {
    show('success', text, options)
  },
  error(text: string, options?: MessageOptions) {
    show('error', text, options)
  },
  warning(text: string, options?: MessageOptions) {
    show('warning', text, options)
  },
  info(text: string, options?: MessageOptions) {
    show('info', text, options)
  }
}
